import { FC, memo } from 'react'
import { Container, SliderContainer, SkeletonRow, SlideInner } from './styles'
import GridCard from '../../common/grid-card'
import { Title, TitleContainer } from '../common/styles'
import { useTranslation } from '../../../hooks/locale'
import { BreadcrumbListTransport } from '../../../models/common/breadcrumb-model'
import { useContinueYourSearchListings } from '../../../hooks/search'
import isEmpty from 'lodash/isEmpty'
import { GridCardSkeleton } from '../../common/card-skeletons'
import { useResponsiveCards } from '../../../hooks/responsive'
import { cardEventCategories, pageTypeEventCategories } from '../../../services/analytics-service/categories'
import { Offer } from '../../../models/common/offers-model'
import { SuitablesModel } from '../../../models/common/suitables-model'
import { EmblaCarousel } from '../../common/embla-carousel'

interface Props {
    url: string
    breadcrumbModel: BreadcrumbListTransport
    offerModel: Offer
    suitablesModel: SuitablesModel
}

const WIDGET_ID = 'continue-search'
const SKELETON_CARD_COUNT = 4

// Runs synchronously as the browser parses this markup, before it paints this section — well before
// React hydrates. The view-count data this widget depends on lives in localStorage (unavailable during
// SSR), so the server and the first client render both default to the skeleton below. If there's no
// view history at all, `fetchContinueYourSearch` (see useContinueYourSearchListings) never even starts
// the listings fetch, so we can hide the reserved skeleton now with certainty instead of collapsing it
// after the fact. If there IS view history, we leave the skeleton in place — the real fetch still has to
// happen over the network, so unlike the no-history case we can't know the outcome synchronously here.
const NO_FLASH_SCRIPT = `
(function () {
    var el = document.getElementById('${WIDGET_ID}');
    if (!el) return;
    try {
        var raw = localStorage.getItem('listing_view_counts');
        var counts = raw ? JSON.parse(raw) : null;
        if (!counts || Object.keys(counts).length === 0) {
            el.style.display = 'none';
        }
    } catch (e) {
        el.style.display = 'none';
    }
})();
`

const ContinueSearchWidget: FC<Props> = ({ url, breadcrumbModel, offerModel, suitablesModel }) => {
    const { listings, isLoading } = useContinueYourSearchListings()
    const { t } = useTranslation()
    const heading = t('pages.home.continueSearch.heading')

    const visibleSlides = useResponsiveCards()
    const slidesToScroll = Math.floor(visibleSlides)

    if (listings === null || isLoading) {
        return (
            <Container id={WIDGET_ID}>
                <TitleContainer>
                    <Title>{heading}</Title>
                </TitleContainer>
                <SkeletonRow>
                    {Array.from({ length: SKELETON_CARD_COUNT }).map((_, i) => (
                        <SlideInner key={`continue-search-skeleton-${i}`}>
                            <GridCardSkeleton />
                        </SlideInner>
                    ))}
                </SkeletonRow>
                <script dangerouslySetInnerHTML={{ __html: NO_FLASH_SCRIPT }} />
            </Container>
        )
    }

    const { objects = [] } = listings

    if (isEmpty(objects)) {
        return null
    }

    return (
        <Container id={WIDGET_ID}>
            <TitleContainer>
                <Title>{heading}</Title>
            </TitleContainer>
            <SliderContainer>
                <EmblaCarousel
                    slides={objects.map((recommendedObject, index) => (
                        <GridCard
                            key={recommendedObject.id}
                            index={index}
                            listingObject={recommendedObject}
                            requestUrl={url}
                            pageName={pageTypeEventCategories.HOME}
                            sectionName={cardEventCategories.sections.CONTINUE_SEARCH}
                            breadcrumbModel={breadcrumbModel}
                            offerModel={offerModel}
                            suitablesModel={suitablesModel}
                            showFeatures={false}
                        />
                    ))}
                    visibleSlides={visibleSlides}
                    slidesToScroll={slidesToScroll}
                    showNav
                    navVariant="top-right"
                    navPosition={{ desktopTop: '-3.8rem', wideTop: '-3.8rem' }}
                />
            </SliderContainer>
        </Container>
    )
}

export default memo(ContinueSearchWidget)
